import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import "./QuantumEchoesDemo.css";

export default function QuantumEchoesDemo() {
  const [qubits, setQubits] = useState(20);
  const [depth, setDepth] = useState(50);
  const [noise, setNoise] = useState(0.02);
  const [data, setData] = useState([]);
  const [running, setRunning] = useState(false);
  const [angle, setAngle] = useState(0);

  useEffect(() => {
    if (!running) return;
    let t = 0;
    const interval = setInterval(() => {
      t++;
      const fidelity = Math.exp(-noise * t) * Math.cos(t / (depth / 2));
      const coherence = Math.abs(fidelity) * 100;
      setAngle((prev) => (prev + 10) % 360);
      setData((d) => [...d.slice(-49), { t, fidelity, coherence }]);
      if (t > depth * 2) setRunning(false);
    }, 120);
    return () => clearInterval(interval);
  }, [running, noise, depth]);

  const avgFidelity =
    data.length > 0
      ? (data.reduce((a, b) => a + b.fidelity, 0) / data.length).toFixed(3)
      : 0;

  return (
    <div className="qe-root">
      <h2 className="qe-title">⚛️ Quantum Echoes Visualization Lab</h2>
      <p className="qe-desc">
        Experience <span className="highlight">quantum coherence</span> decay and revival — a live simulation of Google’s{" "}
        <span className="highlight">Quantum Echoes</span> (2025) breakthrough.
      </p>

      <div className="qe-panels">
        {/* Left: Bloch Sphere */}
        <div className="qe-bloch">
          <svg width="220" height="220" viewBox="0 0 200 200">
            <circle cx="100" cy="100" r="90" stroke="#0ff" strokeWidth="2" fill="rgba(0,255,255,0.1)" />
            <motion.line
              x1="100"
              y1="100"
              x2={100 + 70 * Math.sin((angle * Math.PI) / 180)}
              y2={100 - 70 * Math.cos((angle * Math.PI) / 180)}
              stroke="#ff0"
              strokeWidth="3"
              animate={{ rotate: angle }}
              transition={{ duration: 0.1 }}
            />
            <motion.circle
              cx={100 + 70 * Math.sin((angle * Math.PI) / 180)}
              cy={100 - 70 * Math.cos((angle * Math.PI) / 180)}
              r="6"
              fill="#ff0"
              animate={{ scale: [1, 1.4, 1] }}
              transition={{ duration: 0.4, repeat: Infinity }}
            />
          </svg>
          <p className="qe-label">Bloch Sphere Rotation</p>
        </div>

        {/* Middle: Graph */}
        <div className="qe-graph">
          <svg width="100%" height="200">
            <polyline
              fill="none"
              stroke="#0ff"
              strokeWidth="2"
              points={data
                .map((d, i) => `${(i / 50) * 600},${150 - d.fidelity * 100}`)
                .join(" ")}
            />
          </svg>
          <div className="qe-axis-label">Time →</div>
          <div className="qe-axis-vertical">Fidelity ↑</div>
          <p className="qe-echo-info">
            Echo Strength:{" "}
            <span className="highlight">
              {data.length > 0
                ? (data[data.length - 1].fidelity > 0.8 ? "Strong Revival" : "Weak Revival")
                : "N/A"}
            </span>
          </p>
        </div>

        {/* Right: Live Stats Table */}
        <div className="qe-stats">
          <table>
            <thead>
              <tr>
                <th>t</th>
                <th>Fidelity</th>
                <th>Coherence (%)</th>
              </tr>
            </thead>
            <tbody>
              {data.slice(-6).map((d, i) => (
                <tr key={i}>
                  <td>{d.t}</td>
                  <td>{d.fidelity.toFixed(3)}</td>
                  <td>{d.coherence.toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="qe-metrics">
            Avg Fidelity: <span className="highlight">{avgFidelity}</span>
            <br />
            Qubits: <span className="highlight">{qubits}</span> | Depth:{" "}
            <span className="highlight">{depth}</span> | Noise:{" "}
            <span className="highlight">{noise}</span>
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="qe-controls">
        <label>
          Qubits:
          <input type="number" value={qubits} min="1" max="100" onChange={(e) => setQubits(+e.target.value)} />
        </label>
        <label>
          Circuit Depth:
          <input type="number" value={depth} min="10" max="200" onChange={(e) => setDepth(+e.target.value)} />
        </label>
        <label>
          Noise Level:
          <input type="number" step="0.01" value={noise} min="0" max="0.1" onChange={(e) => setNoise(+e.target.value)} />
        </label>
        <button className="qe-run-btn" onClick={() => { setData([]); setRunning(true); }}>
          {running ? "Running..." : "Run Quantum Echo"}
        </button>
      </div>

      {/* Background Pulse Effect */}
      <div className="qe-bg-pulse" />
    </div>
  );
}
